import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { Control, Controller } from 'react-hook-form';
import { RxCross2 } from 'react-icons/rx';
import { IoChevronDownSharp } from 'react-icons/io5';
import { Option } from 'components/Common/Select';

const isOptionValueEqual = (a: string | number, b: string | number) => String(a) === String(b);

const toSelectedArray = (value: unknown): (string | number)[] => {
  if (Array.isArray(value)) return value;
  if (value == null || value === '') return [];
  return [value as string | number];
};

function SelectDropdownIcon(props: { className?: string }) {
  const { className } = props;
  return <IoChevronDownSharp className={className} style={{ marginRight: '8px' }} />;
}

interface Props {
  name: string;
  control: Control<any>;
  label: string;
  options: Option[];
  isLoading?: boolean;
}

export default function ChurnSignalFilterSelect({ name, control, label, options, isLoading }: Props) {
  const theme = useTheme();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const selected = toSelectedArray(field.value);

        const applyChange = (nextValue: (string | number)[]) => {
          field.onChange([...nextValue]);
        };

        return (
          <FormControl fullWidth size='small'>
            <InputLabel id={`${name}-label`}>{label}</InputLabel>
            <MuiSelect
              labelId={`${name}-label`}
              label={label}
              multiple
              value={selected}
              error={!!error}
              size='small'
              IconComponent={SelectDropdownIcon}
              onChange={(event) => {
                applyChange(toSelectedArray(event.target.value));
              }}
              sx={{
                background: '#fff',
                maxHeight: '40px',
                width: '100%',
                '.MuiOutlinedInput-notchedOutline': { borderColor: '#ececec' }
              }}
              renderValue={(values) => {
                const selectedValues = values as (string | number)[];
                if (selectedValues.length === 0) return null;

                const firstValue = selectedValues[0];
                const firstOption = options.find((o) => isOptionValueEqual(o.value, firstValue));
                const hiddenCount = selectedValues.length - 1;
                const hiddenOptions = selectedValues.slice(1);

                return (
                  <Box display='flex' flexWrap='nowrap' gap={0.5} alignItems='center' overflow='hidden'>
                    {firstOption && (
                      <Chip
                        label={firstOption.label}
                        size='small'
                        sx={{
                          background: theme.palette.primary.light,
                          color: theme.palette.primary.main,
                          borderRadius: '4px',
                          maxWidth: '100%'
                        }}
                        deleteIcon={<RxCross2 size={14} color={theme.palette.primary.main} />}
                        onMouseDown={(event) => event.stopPropagation()}
                        onDelete={(event) => {
                          event.stopPropagation();
                          applyChange(selectedValues.filter((v) => !isOptionValueEqual(v, firstValue)));
                        }}
                      />
                    )}

                    {hiddenCount > 0 && (
                      <Tooltip
                        arrow
                        placement='top'
                        title={hiddenOptions
                          .map((value) => options.find((o) => isOptionValueEqual(o.value, value))?.label)
                          .filter(Boolean)
                          .join(', ')}
                      >
                        <Box
                          sx={{
                            background: theme.palette.primary.light,
                            color: theme.palette.primary.main,
                            borderRadius: '4px',
                            px: 1,
                            flexShrink: 0,
                            fontSize: 12,
                            fontWeight: 500,
                            lineHeight: '24px'
                          }}
                        >
                          +{hiddenCount}
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                );
              }}
              MenuProps={{
                PaperProps: { style: { maxHeight: 250 } }
              }}
            >
              {options.map((option) => {
                const isSelected = selected.some((v) => isOptionValueEqual(v, option.value));

                return (
                  <MenuItem key={option.value} value={option.value}>
                    <Box display='flex' alignItems='center' gap={0.5}>
                      <Checkbox checked={isSelected} />
                      <Typography>{option.label}</Typography>
                    </Box>
                  </MenuItem>
                );
              })}
              {isLoading && (
                <MenuItem disabled>
                  <Typography>Loading...</Typography>
                </MenuItem>
              )}
            </MuiSelect>
            {error && <FormHelperText error>{error.message}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  );
}
